import { MIN_PUBLISH_LEAD_MS, type PublishTiming } from 'fym-shared/src/music24/oneOffPublish';
import {
  SOUNDCLOUD_ERROR_CODES,
  type SerializedSongPublish,
  type SongPublishFacts,
} from 'fym-shared/src/music24/songPublish';

/**
 * The timing choices of the upload dialog (design c3, c4): "On release day" only while that day is
 * ahead (a released song or a song with no date has none; the server needs a release date).
 */
export function songUploadTimings(releaseStart: Date | null, now: Date): PublishTiming[] {
  const hasDay = releaseStart !== null && releaseStart.getTime() - now.getTime() >= MIN_PUBLISH_LEAD_MS;
  return hasDay ? ['now', 'releaseDay', 'later'] : ['now', 'later'];
}

/** The pre-selected timing: "On release day" while it is ahead (c3), else "Now" (c4). */
export function defaultSongUploadTiming(releaseStart: Date | null, now: Date): PublishTiming {
  return songUploadTimings(releaseStart, now).includes('releaseDay') ? 'releaseDay' : 'now';
}

/** True when "Later" has no time or a time less than 15 minutes ahead (c3 error line). */
export function isLaterTooSoon(publishAt: string | null, now: Date): boolean {
  if (publishAt === null) {
    return true;
  }
  const at = Date.parse(publishAt);
  return Number.isNaN(at) || at - now.getTime() < MIN_PUBLISH_LEAD_MS;
}

/** Why the dialog shows the master check (c5) in place of the form, or null when it can upload. */
export type MasterCheck = 'missing' | 'processing' | 'failed' | null;

export function masterCheckOf(facts: SongPublishFacts): MasterCheck {
  return facts.master.state === 'ready' ? null : facts.master.state;
}

/** The sentence of a refused POST (c3 note, c5): one key per server code. */
export type SongUploadError =
  | 'tooSoon'
  | 'notConnected'
  | 'notEnabled'
  | 'alreadyLive'
  | 'masterMissing'
  | 'masterProcessing'
  | 'masterFailed'
  | 'generic';

function causeCode(error: unknown): string | null {
  const record = error as { m24Limit?: { code?: unknown }; cause?: { code?: unknown } } | null;
  const code = record?.m24Limit?.code ?? record?.cause?.code;
  return typeof code === 'string' ? code : null;
}

const ERROR_BY_CODE: Readonly<Record<string, SongUploadError>> = {
  M24_PUBLISH_TIME_INVALID: 'tooSoon',
  [SOUNDCLOUD_ERROR_CODES.notConnected]: 'notConnected',
  [SOUNDCLOUD_ERROR_CODES.autoUploadNotEnabled]: 'notEnabled',
  [SOUNDCLOUD_ERROR_CODES.alreadyLive]: 'alreadyLive',
  M24_NO_MASTER: 'masterMissing',
  M24_MASTER_PROCESSING: 'masterProcessing',
  M24_MASTER_FAILED: 'masterFailed',
};

/** A code that the dialog does not name gives the general sentence. */
export function songUploadErrorOf(error: unknown): SongUploadError {
  const code = causeCode(error);
  return (code === null ? undefined : ERROR_BY_CODE[code]) ?? 'generic';
}

/** The state of the status panel (design c6). */
export type SongUploadStatusKind =
  | 'waiting'
  | 'uploading'
  | 'live'
  | 'private'
  | 'removed'
  | 'failed'
  | 'refused'
  | 'unsure'
  | 'master'
  | 'reconnect';

export function songUploadStatusKind(record: SerializedSongPublish, loginExpired: boolean): SongUploadStatusKind {
  switch (record.status) {
    case 'waiting':
    case 'scheduled':
      return loginExpired ? 'reconnect' : 'waiting';
    case 'uploading':
      return 'uploading';
    case 'published':
      return record.sharing === 'private' ? 'private' : 'live';
    case 'removed':
    case 'canceled':
      return 'removed';
    case 'failed':
      break;
  }
  switch (record.failReason) {
    case 'reconnect':
      return 'reconnect';
    case 'refused':
      return 'refused';
    case 'unsure':
      return 'unsure';
    case 'master':
      return 'master';
    case 'error':
    case null:
      return loginExpired ? 'reconnect' : 'failed';
  }
}
